import { ModelSetTransformer, ModelTransformer } from '../common/common.model';

export interface ExtTdbfgCompliancePopupItem {
    typeID: number;
    pillarID: number;
    pillarName: string;
    userID: number;
    eventDate: string;
    isQTD: number;
    superGroupID: number;
    name: string;
    superGroupName: string;
    score: number;
    oppItems: number;
    scoreThresholdColour: string;
    scoreTrendImage: string;
    complianceScore: number;
    complianceOppItems: number;
    complianceScoreThresholdColour: string;
    complianceScoreTrendImage: string;
    autoScore: number;
    autoOppItems: number;
    autoScoreThresholdColour: string;
    autoScoreTrendImage: string;
    resiScore: number;
    resiOppItems: number;
    resiScoreThresholdColour: string;
    resiScoreTrendImage: string;
    percentOfTeam: number;
}

export interface TdbfgCompliancePopupValue {
    value: number;
    oppItems: number;
    thresholdColor: string;
    trendClass: string;
}

export interface TdbfgCompliancePopupUser {
    id: number;
    username: string;
    superGroupId: number;
    superGroupName: string;
    score: TdbfgCompliancePopupValue;
    percentOfTeam: number;
    compliance: TdbfgCompliancePopupValue;
    auto: TdbfgCompliancePopupValue;
    resi: TdbfgCompliancePopupValue;
}

export interface TdbfgCompliancePopupItem {
    name: string;
    user: TdbfgCompliancePopupUser;
    team: TdbfgCompliancePopupUser[];
    overall: TdbfgCompliancePopupUser;
}

interface IntTdbfgCompliancePopupPillar {
    items: ExtTdbfgCompliancePopupItem[];
}

export interface TdbfgCompliancePopup {
    items: TdbfgCompliancePopupItem[];
}

class TdbfgCompliancePopupValueModelTransformer
    implements
        ModelTransformer<
            TdbfgCompliancePopupValue,
            ExtTdbfgCompliancePopupItem
        >
{
    constructor(
        private readonly _type: 'score' | 'compliance' | 'auto' | 'resi',
    ) {}

    transform(model: ExtTdbfgCompliancePopupItem): TdbfgCompliancePopupValue {
        let value = `${this._type}Score`;
        let oppItems = `${this._type}OppItems`;
        let thresholdColor = `${this._type}ScoreThresholdColour`;
        let trendClass = `${this._type}ScoreTrendImage`;
        if (this._type === 'score') {
            value = 'score';
            oppItems = 'oppItems';
            thresholdColor = 'scoreThresholdColour';
            trendClass = 'scoreTrendImage';
        }
        return {
            value: model[value],
            oppItems: model[oppItems],
            thresholdColor: model[thresholdColor],
            trendClass: model[trendClass].toLowerCase(),
        };
    }
}

class TdbfgCompliancePopupUserModelTransformer
    implements
        ModelTransformer<TdbfgCompliancePopupUser, ExtTdbfgCompliancePopupItem>
{
    transform(model: ExtTdbfgCompliancePopupItem): TdbfgCompliancePopupUser {
        const score = new TdbfgCompliancePopupValueModelTransformer(
            'score',
        ).transform(model);
        const compliance = new TdbfgCompliancePopupValueModelTransformer(
            'compliance',
        ).transform(model);
        const auto = new TdbfgCompliancePopupValueModelTransformer(
            'auto',
        ).transform(model);
        const resi = new TdbfgCompliancePopupValueModelTransformer(
            'resi',
        ).transform(model);
        return {
            id: model.userID,
            username: model.name,
            superGroupId: model.superGroupID,
            superGroupName: model.superGroupName,
            score,
            percentOfTeam:
                model.percentOfTeam == null ? null : model.percentOfTeam / 100,
            compliance,
            auto,
            resi,
        };
    }
}

class TdbfgCompliancePopupUserModelSetTransformer extends ModelSetTransformer<
    TdbfgCompliancePopupUser,
    ExtTdbfgCompliancePopupItem
> {
    protected newTransformer(): ModelTransformer<
        TdbfgCompliancePopupUser,
        ExtTdbfgCompliancePopupItem
    > {
        return new TdbfgCompliancePopupUserModelTransformer();
    }
}

class TdbfgCompliancePopupItemModelTransformer
    implements
        ModelTransformer<
            TdbfgCompliancePopupItem,
            IntTdbfgCompliancePopupPillar
        >
{
    transform(model: IntTdbfgCompliancePopupPillar): TdbfgCompliancePopupItem {
        const users_ = model.items.filter((value, index) => index !== 1);
        const users =
            new TdbfgCompliancePopupUserModelSetTransformer().transform(users_);

        const overall_ = model.items.length > 2 ? model.items[1] : null;
        let overall;
        if (overall_) {
            overall = new TdbfgCompliancePopupUserModelTransformer().transform(
                overall_,
            );
        }

        return {
            name: model.items[0].pillarName,
            user: users.length !== 0 ? users[0] : null,
            team: users.slice(1),
            overall,
        };
    }
}

class TdbfgCompliancePopupItemModelSetTransformer extends ModelSetTransformer<
    TdbfgCompliancePopupItem,
    IntTdbfgCompliancePopupPillar
> {
    protected newTransformer(): ModelTransformer<
        TdbfgCompliancePopupItem,
        IntTdbfgCompliancePopupPillar
    > {
        return new TdbfgCompliancePopupItemModelTransformer();
    }
}

export class TdbfgCompliancePopupModelTransformer
    implements
        ModelTransformer<TdbfgCompliancePopup, ExtTdbfgCompliancePopupItem[]>
{
    transform(model: ExtTdbfgCompliancePopupItem[]): TdbfgCompliancePopup {
        const pillarsIds = [];
        const pillars_: IntTdbfgCompliancePopupPillar[] = [];
        model.forEach((value) => {
            const index = pillarsIds.indexOf(value.pillarID);
            if (index !== -1) {
                pillars_[index].items.push(value);
                return;
            }
            pillarsIds.push(value.pillarID);
            pillars_.push({
                items: [value],
            });
        });

        const items =
            new TdbfgCompliancePopupItemModelSetTransformer().transform(
                pillars_,
            );
        return {
            items,
        };
    }
}

export function validateTdbfgCompliancePopup(
    model: TdbfgCompliancePopup,
): void {
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    const valid =
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        model != null && model.items != null && model.items.length !== 0;
    if (!valid) {
        throw new Error('TdbfgCompliancePopup not valid');
    }
}
